import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';

import { ErrorTypeEnum } from '../../../../main/constants/error-type.ts';
import { TrackingEventEnum } from '../../../../main/constants/tracking-event.ts';
import { ThunkApiConfigType } from '../../../../main/store/main.store.ts';
import { getLocaleUtil } from '../../../../main/utils/locale.util.ts';
import { getSentryUtil } from '../../../../main/utils/monitoring/sentry.util.ts';
import { getToasterUtil } from '../../../../main/utils/toaster.util.ts';
import { getTrackerUtil } from '../../../../main/utils/trackers/tracker.util.ts';

import { deleteParentAccountMutationRequest } from '../../../requests/delete-parent-account.mutation.ts';

import { signOutThunk } from '../../auth/thunks/sign-out.thunk.ts';

import { ACCOUNTS_SLICE_NAME, AccountsSliceState } from '../accounts.slice.types.ts';

const THUNK_NAME = `${ACCOUNTS_SLICE_NAME}/deleteParentAccount`;

export const deleteParentAccountThunk = createAsyncThunk<void, void, ThunkApiConfigType>(
  THUNK_NAME,
  async (_, thunkAPI) => {
    const result = await deleteParentAccountMutationRequest();

    if (!result?.success) {
      const errorMessage = result.message ?? getLocaleUtil().select('accounts:delete_parent_account_failed');
      getToasterUtil().error(errorMessage);
      const error = new Error(errorMessage);
      error.name = ErrorTypeEnum.DELETE_PARENT_ACCOUNT_ERROR;

      getSentryUtil().trackError(error, {
        thunk: THUNK_NAME,
        errorType: ErrorTypeEnum.DELETE_PARENT_ACCOUNT_ERROR,
      });
      getTrackerUtil().track({
        event: TrackingEventEnum.PARENT_ACCOUNT_DELETION_FAILED,
      });
      throw error;
    }

    getToasterUtil().success(getLocaleUtil().select('accounts:delete_parent_account_success'));
    const currentUser = thunkAPI.getState().accounts.currentAccount;
    getTrackerUtil().track({
      event: TrackingEventEnum.PARENT_ACCOUNT_DELETED,
      data: {
        parentUUID: currentUser?.userId,
      },
    });

    await thunkAPI.dispatch(signOutThunk()).unwrap();
  }
);

export const deleteParentAccountThunkExtraReducers = (builder: ActionReducerMapBuilder<AccountsSliceState>): void => {
  builder.addCase(deleteParentAccountThunk.pending, (state) => {
    state.isDeleteAccountLoading = true;
  });

  builder.addCase(deleteParentAccountThunk.fulfilled, (state) => {
    state.isDeleteAccountLoading = false;
  });

  builder.addCase(deleteParentAccountThunk.rejected, (state) => {
    state.isDeleteAccountLoading = false;
  });
};
